import type {
  EmailMarketingOverview,
  EmailMarketingResource,
} from '../gen/cortado/v1/admin_pb'
import { EmailMarketingAudienceEnum } from '../gen/cortado/v1/settings_pb'

// Display order for the audiences, matching the backend's segment order.
export const EMAIL_MARKETING_AUDIENCES = [
  EmailMarketingAudienceEnum.CURRENT_CUSTOMERS,
  EmailMarketingAudienceEnum.PREVIOUS_CUSTOMERS,
  EmailMarketingAudienceEnum.NEVER_SUBSCRIBED,
  EmailMarketingAudienceEnum.WAITLIST,
] as const

const audienceCopy: Record<number, { label: string; description: string }> = {
  [EmailMarketingAudienceEnum.CURRENT_CUSTOMERS]: {
    label: 'Current customers',
    description: 'Members of orgs that are trialing, active, past due or comped.',
  },
  [EmailMarketingAudienceEnum.PREVIOUS_CUSTOMERS]: {
    label: 'Previous customers',
    description: 'Members of orgs that are read-only or expired, and of no current org.',
  },
  [EmailMarketingAudienceEnum.NEVER_SUBSCRIBED]: {
    label: 'Signed up, never subscribed',
    description: 'Users with no org, or whose orgs never started a subscription.',
  },
  [EmailMarketingAudienceEnum.WAITLIST]: {
    label: 'Waitlist',
    description: 'Waitlist signups without a Cortado account.',
  },
}

export function isAudienceSelected(audience?: EmailMarketingAudienceEnum) {
  return audience !== undefined && audience in audienceCopy
}

export function audienceLabel(audience?: EmailMarketingAudienceEnum) {
  return isAudienceSelected(audience) ? audienceCopy[audience as number].label : 'No audience'
}

export function audienceDescription(audience?: EmailMarketingAudienceEnum) {
  return isAudienceSelected(audience) ? audienceCopy[audience as number].description : ''
}

export function audienceMemberCount(
  overview: EmailMarketingOverview | undefined,
  audience?: EmailMarketingAudienceEnum,
) {
  return (
    overview?.audienceSegments.find((segment) => segment.audience === audience)?.memberCount ?? 0
  )
}

// Every audience has a segment that exists in Resend.
export function audienceSegmentsReady(overview: EmailMarketingOverview | undefined) {
  if (!overview) return false
  return EMAIL_MARKETING_AUDIENCES.every((audience) =>
    overview.audienceSegments.some(
      (segment) => segment.audience === audience && segment.segment?.configured,
    ),
  )
}

// IDs of the Resend segments and topics Cortado creates and keeps in sync.
export function managedResourceIds(overview: EmailMarketingOverview | undefined) {
  const ids = new Set<string>()
  if (!overview) return ids
  for (const segment of overview.audienceSegments) {
    if (segment.segment?.id) ids.add(segment.segment.id)
  }
  if (overview.productUpdatesTopic?.id) ids.add(overview.productUpdatesTopic.id)
  if (overview.announcementsTopic?.id) ids.add(overview.announcementsTopic.id)
  return ids
}

// Resources in Resend that Cortado does not manage, such as the retired
// "Cortado Users" segment or a topic made by hand.
export function unmanagedResources(
  resources: EmailMarketingResource[],
  managedIds: Set<string>,
) {
  return resources.filter((resource) => !managedIds.has(resource.id))
}
